/**
 * Shell furniture: the command palette, the keyboard sheet and the account menu.
 *
 * The console is meant to be driven from the keyboard. Shortcuts are fixed and not rebindable —
 * an ops desk shares muscle memory, and a shortcut that means something different at the next
 * chair is worse than no shortcut at all.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, Icon, Kicker, StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { Modal } from '../components/feedback';
import type { IconName } from '../components/icons';
import { reauthenticate } from '../lib/auth/reauth';
import type { AdminModule } from './modules';
import type { ActingScope } from './Shell';
import { SECTION_ROWS } from './sectionKeys';

/* ────────────────────────────  Command palette  ──────────────────────────── */

export function matchModules(query: string, reachable: readonly AdminModule[]): AdminModule[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...reachable];
  return reachable.filter((m) => `${m.label} ${m.group} ${m.title} ${m.path}`.toLowerCase().includes(q));
}

export interface CommandPaletteProps {
  open: boolean;
  /** Only what this scope can reach — the palette is not a way around the nav filter. */
  modules: readonly AdminModule[];
  onGo: (module: AdminModule) => void;
  onClose: () => void;
}

export function CommandPalette({ open, modules, onGo, onClose }: CommandPaletteProps) {
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(0);
  const results = useMemo(() => matchModules(q, modules), [q, modules]);

  useEffect(() => {
    if (open) {
      setQ('');
      setFocus(0);
    }
  }, [open]);

  useEffect(() => {
    setFocus(0);
  }, [q]);

  if (!open) return null;

  const onKey = (e: { key: string; preventDefault: () => void }) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocus((f) => Math.min(results.length - 1, f + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocus((f) => Math.max(0, f - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[focus];
      if (target) onGo(target);
    }
  };

  return (
    <Modal title="Jump to a module" width={620} onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }} onKeyDown={onKey}>
        <Input
          id="command-palette"
          density="admin"
          icon="search"
          value={q}
          onChange={setQ}
          placeholder="Type a module, a section or a path"
        />
        <div style={{ maxHeight: 340, overflowY: 'auto', display: 'grid' }}>
          {results.length === 0 ? (
            <span style={{ padding: 'var(--sp-md)', fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
              Nothing matches — and nothing is hidden from you here that the nav would show.
            </span>
          ) : (
            results.map((m, i) => (
              <button
                key={m.key}
                type="button"
                onMouseEnter={() => setFocus(i)}
                onClick={() => onGo(m)}
                aria-current={i === focus ? 'true' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-md)',
                  padding: 'var(--sp-sm) var(--sp-md)',
                  border: 'none',
                  borderRadius: 'var(--r-tile)',
                  background: i === focus ? 'var(--c-saffron-50)' : 'transparent',
                  color: 'var(--c-ink)',
                  font: 'inherit',
                  fontSize: 'var(--fs-admin-ui)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <Icon name={m.icon} size={16} style={{ color: i === focus ? 'var(--c-saffron)' : 'var(--c-ink3)' }} />
                <span style={{ flex: 1, minWidth: 0 }}>{m.label}</span>
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{m.group}</span>
                <span className="mono-num" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                  {m.path}
                </span>
              </button>
            ))
          )}
        </div>
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          ↑ ↓ move · ⏎ opens · Esc closes
        </span>
      </div>
    </Modal>
  );
}

/* ────────────────────────────  Keyboard sheet  ──────────────────────────── */

export const SHORTCUTS: readonly (readonly [string, readonly (readonly [string, string])[]])[] = [
  [
    'Global',
    [
      ['Jump to a module', '⌘K'],
      ['Focus the table search', '/'],
      ['Close a detail view or dialog', 'Esc'],
      ['This sheet', '?'],
      ['Previous / next page of a paged list', '[ / ]'],
    ],
  ],
  ['Sections', SECTION_ROWS],
  [
    'Any queue',
    [
      ['Move down / up a row', 'j / k'],
      ['Open the focused row', '⏎'],
    ],
  ],
  [
    'Money screens',
    [
      ['Type-to-confirm field', 'Tab to reach, never pre-filled'],
      ['Four-eyes approval', 'A second person, never a second click'],
    ],
  ],
];

export function KeyboardOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <Modal
      title="Keyboard"
      width={720}
      onClose={onClose}
      actions={
        <Button size="md" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-lg)' }}>
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
          This console is meant to be driven from the keyboard. Shortcuts are fixed and not
          rebindable — an ops desk shares muscle memory, and a shortcut that means something
          different at the next chair is worse than none.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--sp-lg)', alignItems: 'start' }}>
          {SHORTCUTS.map(([group, rows]) => (
            <div key={group} style={{ display: 'grid', gap: 6 }}>
              <Kicker>{group}</Kicker>
              {rows.map(([label, keys]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-sm)' }}>
                  <span style={{ flex: 1, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>{label}</span>
                  <kbd
                    className="mono-num"
                    style={{
                      padding: '2px 7px',
                      border: '1px solid var(--c-border-strong)',
                      borderRadius: 'var(--r-tile)',
                      background: 'var(--c-sunk)',
                      fontSize: 'var(--fs-admin-colhead)',
                      color: 'var(--c-ink)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {keys}
                  </kbd>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

/* ────────────────────────────  Account menu  ──────────────────────────── */

export interface AccountMenuProps {
  name: string;
  email: string;
  scope: ActingScope;
  onOpenAccount: () => void;
  onOpenKeys: () => void;
  onSignOut: () => void;
}

const ACCOUNT_LINKS: readonly { key: string; label: string; note: string; icon: IconName }[] = [
  { key: 'account', label: 'Account settings', note: 'Name, photo, phone, locale', icon: 'settings' },
  { key: 'security', label: 'Password & security', note: 'Sessions, TOTP, recovery codes', icon: 'key-round' },
  { key: 'notify', label: 'Notification preferences', note: 'Some alerts cannot be silenced', icon: 'bell' },
];

export function AccountMenu({ name, email, scope, onOpenAccount, onOpenKeys, onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          height: 40,
          padding: '0 var(--sp-sm)',
          border: `1px solid ${open ? 'var(--c-saffron)' : 'transparent'}`,
          background: open ? 'var(--c-saffron-50)' : 'transparent',
          borderRadius: 'var(--r-tile)',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <Avatar name={name} size={28} />
        <span style={{ display: 'grid', textAlign: 'left', lineHeight: 1.2 }}>
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink)' }}>{name}</span>
          <span className="mono-num" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-saffron-text)' }}>
            {scope}
          </span>
        </span>
        <Icon name="chevron-down" size={14} style={{ color: 'var(--c-ink3)' }} />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 48,
            right: 0,
            width: 340,
            zIndex: 80,
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r-card)',
            boxShadow: 'var(--sh-lg)',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center', padding: 'var(--sp-lg)', background: 'var(--c-sunk)' }}>
            <Avatar name={name} size={44} />
            <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--fs-admin-card-heading)',
                  fontWeight: 'var(--fw-semibold)',
                  color: 'var(--c-ink)',
                }}
              >
                {name}
              </span>
              <span
                className="mono-num"
                style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {email}
              </span>
              {/* The founder scope reads as a warning, not a boast — it is the blast radius. */}
              <StatusChip tone={scope === 'superadmin' ? 'error' : 'info'} label={scope} icon="shield" />
            </div>
          </div>
          {ACCOUNT_LINKS.map((l) => (
            <button
              key={l.key}
              type="button"
              role="menuitem"
              onClick={() => choose(onOpenAccount)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-md)',
                width: '100%',
                padding: 'var(--sp-md) var(--sp-lg)',
                border: 'none',
                borderTop: '1px solid var(--c-border)',
                background: 'transparent',
                cursor: 'pointer',
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              <Icon name={l.icon} size={18} style={{ color: 'var(--c-saffron)' }} />
              <span style={{ flex: 1, display: 'grid' }}>
                <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>{l.label}</span>
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{l.note}</span>
              </span>
              <Icon name="chevron-right" size={14} style={{ color: 'var(--c-ink3)' }} />
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(onOpenKeys)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-md)',
              width: '100%',
              padding: 'var(--sp-md) var(--sp-lg)',
              border: 'none',
              borderTop: '1px solid var(--c-border)',
              background: 'transparent',
              cursor: 'pointer',
              font: 'inherit',
              textAlign: 'left',
            }}
          >
            <Icon name="keyboard" size={18} style={{ color: 'var(--c-saffron)' }} />
            <span style={{ flex: 1, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink)' }}>Keyboard shortcuts</span>
            <kbd className="mono-num" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
              ?
            </kbd>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose(onSignOut)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-md)',
              width: '100%',
              padding: 'var(--sp-md) var(--sp-lg)',
              border: 'none',
              borderTop: '1px solid var(--c-border)',
              background: 'transparent',
              cursor: 'pointer',
              font: 'inherit',
              color: 'var(--c-error-text)',
              textAlign: 'left',
            }}
          >
            <Icon name="log-out" size={18} />
            <span style={{ flex: 1, fontSize: 'var(--fs-admin-ui)' }}>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────  Idle lock  ──────────────────────────── */

export interface IdleLockProps {
  open: boolean;
  name: string;
  email: string;
  onUnlock: () => void;
  onSignOut: () => void;
  /** Why it is up: thirty idle minutes, or an action the server will only take after a fresh sign-in. */
  reason?: 'idle' | 'step_up';
}

/**
 * The idle-lock overlay — `docs/05-admin/02` §2.3.
 *
 * It covers the console rather than replacing it, and it says whose session it is holding: the
 * point is that the person who walks back to the desk is the person who left it, and the person
 * who sits down at somebody else's unlocked console is told whose it is rather than inheriting it.
 *
 * There is a way out that is not the passphrase — **Sign out** — because a lock with only one
 * door is a lock somebody props open.
 */
export function IdleLock({ open, name, email, onUnlock, onSignOut, reason = 'idle' }: IdleLockProps) {
  const [passphrase, setPassphrase] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setPassphrase('');
      setCode('');
      setError('');
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const resume = async () => {
    setBusy(true);
    setError('');
    const failure = await reauthenticate(email, passphrase, code);
    setBusy(false);
    if (failure) setError(failure);
    else onUnlock();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Console locked"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--sp-lg)',
        background: 'var(--c-overlay)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gap: 'var(--sp-md)',
          width: '100%',
          maxWidth: 380,
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-card)',
          boxShadow: 'var(--sh-lg)',
          padding: 'var(--sp-xxl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)' }}>
          <Icon name="lock" size={20} style={{ color: 'var(--c-saffron)' }} />
          <span style={{ display: 'grid' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-admin-h2)', color: 'var(--c-ink)' }}>
              {reason === 'step_up' ? 'Confirm it is you to continue' : 'Locked after 12 hours idle'}
            </span>
            <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
              {name} · {email}
            </span>
          </span>
        </div>

        <Input
          density="admin"
          type="password"
          label="Password"
          value={passphrase}
          onChange={setPassphrase}
          error={error || undefined}
        />
        <Input
          density="admin"
          label="Authenticator code"
          value={code}
          onChange={setCode}
          inputMode="numeric"
          helper="Only if you set up an authenticator — leave it blank otherwise"
        />

        <Button size="lg" fullWidth disabled={busy} onClick={resume}>
          {busy ? 'Checking…' : 'Unlock'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onSignOut}>
          Sign out instead
        </Button>
      </div>
    </div>
  );
}
